import React from "react";
import { Heart } from "lucide-react";
import { useFavorites } from "@/hooks/useFavorites";

/**
 * Heart toggle for a listing. Works logged-out (localStorage) and logged-in
 * (server-backed). Optimistic update via useFavorites.toggle().
 *
 * Variant "chip": small pill-style; "icon": circular icon-only (default).
 */
export default function FavoriteButton({ listingId, variant = "icon", className = "" }) {
  const { toggle, isFavorite } = useFavorites();
  const active = isFavorite(listingId);

  if (variant === "chip") {
    return (
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(listingId); }}
        data-testid={`favorite-btn-${listingId}`}
        aria-pressed={active}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
          active
            ? "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
            : "bg-white text-slate-700 border border-soft hover:border-red-300 hover:text-red-600"
        } ${className}`}
        title={active ? "Remove from favourites" : "Save to favourites"}
      >
        <Heart className={`w-3.5 h-3.5 ${active ? "fill-red-500" : ""}`} />
        {active ? "Saved" : "Save"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(listingId); }}
      data-testid={`favorite-btn-${listingId}`}
      aria-pressed={active}
      aria-label={active ? "Remove from favourites" : "Save to favourites"}
      className={`w-9 h-9 grid place-items-center rounded-full backdrop-blur bg-white/85 hover:bg-white text-slate-700 shadow-sm transition-colors ${className}`}
    >
      <Heart className={`w-4 h-4 ${active ? "fill-red-500 text-red-500" : ""}`} />
    </button>
  );
}
